import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.addEventListener('change', this.#handleStickyVariantChange, { signal });

    if (this.dataset.defaultUnselected === 'true' && !this.#hasVariantSelectionInUrl()) {
      this.#clearVariantSelection();
    } else {
      this.#syncStickyVariantStateFromProductForm();
    }

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const previousStickySelect = this.querySelector('.sticky-add-to-cart__variant-select');
    const selectedStickyVariantId =
      previousStickySelect instanceof HTMLSelectElement ? previousStickySelect.value : this.dataset.currentVariantId || '';

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    this.#restoreStickyVariantSelection(variant?.id?.toString() || selectedStickyVariantId);
    this.#syncStickyButtonState(variant);

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Routes sticky dropdown changes back through the main variant picker so the
   * product form remains the single source of truth for pricing and availability.
   * @param {Event} event
   */
  #handleStickyVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.classList.contains('sticky-add-to-cart__variant-select')) {
      return;
    }

    const selectedOption = select.options[select.selectedIndex];
    const variantOptions = selectedOption?.dataset.variantOptions;
    if (!variantOptions) return;

    let optionValues;
    try {
      optionValues = JSON.parse(variantOptions);
    } catch (error) {
      console.error('Failed to parse sticky variant options:', error);
      return;
    }

    this.#syncMainVariantPicker(optionValues);
  };

  /**
   * Applies a sticky-bar variant selection to the primary variant picker and
   * dispatches one change event so the theme performs its normal section refresh.
   * @param {string[]} optionValues
   */
  #syncMainVariantPicker(optionValues) {
    const productForm = this.#getProductForm();
    const variantPicker = productForm?.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker || !Array.isArray(optionValues) || optionValues.length === 0) return;

    const controls = Array.from(variantPicker.querySelectorAll('fieldset, select'));
    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let finalTarget = null;

    optionValues.forEach((optionValue, index) => {
      const control = controls[index];
      if (!optionValue || !control) return;

      if (control instanceof HTMLFieldSetElement) {
        const radio = Array.from(control.querySelectorAll('input[type="radio"]')).find(
          (input) => input instanceof HTMLInputElement && input.value === optionValue
        );
        if (!(radio instanceof HTMLInputElement)) return;

        variantPicker.updateSelectedOption(radio);
        finalTarget = radio;
        return;
      }

      if (control instanceof HTMLSelectElement) {
        const matchingOption = Array.from(control.options).find((option) => option.value === optionValue);
        if (!matchingOption) return;

        control.value = matchingOption.value;
        variantPicker.updateSelectedOption(control);
        finalTarget = control;
      }
    });

    if (!finalTarget) return;
    finalTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Updates the sticky variant select when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const stickyVariantSelect = this.querySelector('.sticky-add-to-cart__variant-select');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!(stickyVariantSelect instanceof HTMLSelectElement) || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('select option[selected], fieldset input:checked')
    )
      .map((option) => {
        if (option instanceof HTMLOptionElement) return option.value;
        if (option instanceof HTMLInputElement) return option.value;
        return '';
      })
      .filter((value) => value !== '');

    const matchingOption = Array.from(stickyVariantSelect.options).find((option) => {
      const variantOptions = option.dataset.variantOptions;
      if (!variantOptions) return false;

      try {
        const parsedOptions = JSON.parse(variantOptions);
        return JSON.stringify(parsedOptions) === JSON.stringify(selectedOptions);
      } catch (_error) {
        return false;
      }
    });

    if (matchingOption) {
      stickyVariantSelect.value = matchingOption.value;
    }
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Matches the main product form behavior so shared product links can still
   * preselect a variant while direct PDP visits start in an unselected state.
   * @returns {boolean}
   */
  #hasVariantSelectionInUrl() {
    const params = new URL(window.location.href).searchParams;
    return params.has('variant') || params.has('option_values');
  }

  /**
   * Resets the sticky controls to the same unselected state as the main ATC.
   */
  #clearVariantSelection() {
    const stickyVariantSelect = this.querySelector('.sticky-add-to-cart__variant-select');
    if (stickyVariantSelect instanceof HTMLSelectElement) {
      stickyVariantSelect.value = '';
      for (const option of stickyVariantSelect.options) {
        const isSelected = option.hasAttribute('data-placeholder-option');
        option.selected = isSelected;
        option.toggleAttribute('selected', isSelected);
      }
    }

    this.dataset.currentVariantId = '';
    this.refs.addToCartButton.disabled = true;
    const buttonLabel = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
    if (buttonLabel) {
      buttonLabel.textContent = 'Choose Option';
    }
    this.refs.quantityDisplay.style.display = 'none';
  }

  /**
   * On direct PDP loads with a variant URL, make the sticky bar reflect the
   * product form's active variant instead of trusting the initial HTML alone.
   */
  #syncStickyVariantStateFromProductForm() {
    const productForm = this.#getProductForm();
    const activeVariantId = productForm?.querySelector('input[name="id"]')?.value || this.dataset.currentVariantId || '';
    if (!activeVariantId) return;

    this.dataset.currentVariantId = activeVariantId;
    this.#restoreStickyVariantSelection(activeVariantId);

    const addToCartButtonDisabled = this.#targetAddToCartButton?.disabled ?? false;
    const buttonLabel = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
    if (!buttonLabel) return;

    this.refs.addToCartButton.disabled = addToCartButtonDisabled;
    if (addToCartButtonDisabled) {
      buttonLabel.textContent = window.Theme?.translations?.soldOut || 'Sold out';
      this.refs.quantityDisplay.style.display = 'none';
      return;
    }

    buttonLabel.textContent = window.Theme?.translations?.addToCart || 'Add to cart';
    this.#updateButtonText();
  }

  /**
   * The sticky bar HTML is rendered from the original page request, so after a
   * client-side variant change we need to re-apply the live button state here.
   * @param {ProductVariant | null} variant
   */
  #syncStickyButtonState(variant) {
    const buttonLabel = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
    if (!buttonLabel) return;

    if (!variant?.id) {
      this.refs.addToCartButton.disabled = true;
      buttonLabel.textContent = 'Choose Option';
      this.refs.quantityDisplay.style.display = 'none';
      return;
    }

    if (variant.available === false) {
      this.refs.addToCartButton.disabled = true;
      buttonLabel.textContent = window.Theme?.translations?.soldOut || 'Sold out';
      this.refs.quantityDisplay.style.display = 'none';
      return;
    }

    this.refs.addToCartButton.disabled = false;
    buttonLabel.textContent = window.Theme?.translations?.addToCart || 'Add to cart';
    this.#updateButtonText();
  }

  /**
   * Keeps the sticky dropdown aligned with the active variant after the bar is
   * re-rendered from server HTML that may still represent the initial page state.
   * @param {string} variantId
   */
  #restoreStickyVariantSelection(variantId) {
    const stickyVariantSelect = this.querySelector('.sticky-add-to-cart__variant-select');
    if (!(stickyVariantSelect instanceof HTMLSelectElement) || !variantId) return;

    const matchingOption = Array.from(stickyVariantSelect.options).find((option) => option.value === variantId);
    if (!matchingOption) return;

    stickyVariantSelect.value = matchingOption.value;
    for (const option of stickyVariantSelect.options) {
      const isSelected = option === matchingOption;
      option.selected = isSelected;
      option.toggleAttribute('selected', isSelected);
    }
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
